import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';

const ChartCard = styled.div`
  background: ${(props) => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  padding: 1rem 1rem 0.5rem;
  margin-bottom: 1.5rem;
`;

const ChartTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin: 0 0 0.35rem;
`;

const BalanceImpactNote = styled.p`
  font-size: 0.72rem;
  font-style: italic;
  color: ${(props) => props.theme.mutedText};
  margin: 0 0 0.6rem;
  line-height: 1.35;
`;

const ChartWrap = styled.div`
  width: 100%;
  height: 120px;
  @media (min-width: 720px) {
    height: 240px;
  }
`;

const NoWagerHint = styled.p`
  font-size: 0.8rem;
  color: ${(props) => props.theme.mutedText};
  margin: 0 0 0.5rem;
`;

/**
 * @param {{ points: { date: number, balanceDollars: number }[], hasWagerHistory: boolean }} props
 */
const TrackedBalanceChart = ({ points, hasWagerHistory }) => {
  const { theme } = useTheme();

  const tickFormatter = (ts) => {
    try {
      return format(new Date(ts), 'MMM d');
    } catch {
      return '';
    }
  };

  const tooltipFormatter = (value) => [`$${Number(value).toFixed(2)}`, 'Balance'];

  const tooltipLabelFormatter = (ts) => {
    try {
      return format(new Date(ts), 'MMM d, yyyy · h:mm a');
    } catch {
      return String(ts);
    }
  };

  return (
    <ChartCard theme={theme}>
      <ChartTitle theme={theme}>Simulated balance</ChartTitle>
      <BalanceImpactNote theme={theme}>
        Tracked picks must include a bet amount and odds to impact balance.
      </BalanceImpactNote>
      {!hasWagerHistory && (
        <NoWagerHint theme={theme}>
          Place a simulated wager on a tracked pick to see movement here.
        </NoWagerHint>
      )}
      <ChartWrap>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={points}
            margin={{ top: 4, right: 8, left: 0, bottom: 2 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} />
            <XAxis
              dataKey="date"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={tickFormatter}
              stroke={theme.mutedText}
              tick={{ fill: theme.mutedText, fontSize: 11 }}
            />
            <YAxis
              domain={['auto', 'auto']}
              tickFormatter={(v) => `$${v}`}
              stroke={theme.mutedText}
              tick={{ fill: theme.mutedText, fontSize: 11 }}
              width={56}
            />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={tooltipLabelFormatter}
              contentStyle={{
                background: theme.cardBg,
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                color: theme.primaryText,
              }}
            />
            <Line
              type="stepAfter"
              dataKey="balanceDollars"
              stroke={theme.primary}
              strokeWidth={2}
              dot={hasWagerHistory ? { r: 3, fill: theme.primary } : false}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartWrap>
    </ChartCard>
  );
};

export default TrackedBalanceChart;
